import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import agentsService from '../../services/agentsService';
import { authService } from '../../services/authService';
// NOTE: SubscriptionManagement was already deliberately disabled in the
// pre-reskin version ("Website Subscription Management - Temporarily
// hidden") - preserving that decision here rather than re-enabling it.

// Voyager Pro reskin (Figma AgentProfilePage) - shared by BOTH agency and
// independent agents. Confirmed backend/routes/agents.js's GET/PUT /profile
// has zero role restriction (already scoped by `WHERE id = req.user.id`,
// same pattern as Quotes/Itineraries/etc.) - the separate IndependentProfile.jsx
// + independentProfileService.js + backend/routes/independent/profile.js were
// a parallel, never-fully-adopted implementation; left in place unused.
//
// Net-new vs the pre-reskin version (per Figma, user-approved during this
// session's broader pass): website/tagline fields, 4 notification toggles,
// and a profile-completeness meter - all backed by new `agents` table
// columns (website, tagline, notify_payment_received, notify_reminder_sent,
// notify_quote_viewed, notify_client_message). Also ported the in-app
// change-password feature independent agents already had (IndependentProfile.jsx)
// but agency agents never did, via a new PUT /api/agents/profile/password
// endpoint mirroring the independent one. The 8-option background-theme
// picker is real-only functionality Figma has no equivalent for - preserved
// unchanged.

const backgroundThemes = [
  { id: 'none', name: 'None (Clean)', preview: 'transparent' },
  { id: 'ocean-gradient', name: 'Ocean Breeze', preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'sunset-gradient', name: 'Sunset Glow', preview: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'forest-gradient', name: 'Forest Mist', preview: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { id: 'lavender-gradient', name: 'Lavender Dreams', preview: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
  { id: 'golden-gradient', name: 'Golden Hour', preview: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
  { id: 'aurora-gradient', name: 'Aurora', preview: 'linear-gradient(135deg, #3f2b96 0%, #a8c0ff 100%)' },
  { id: 'geometric-pattern', name: 'Geometric', preview: 'url(data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E)' },
];

const initials = (first, last) => `${(first || '?').charAt(0)}${(last || '').charAt(0)}`.toUpperCase();

function completeness(p) {
  const checks = [
    [!!p.firstName && !!p.lastName, 'Full name'],
    [!!p.email, 'Email address'],
    [!!p.phone, 'Phone number'],
    [!!p.businessName, 'Business name'],
    [!!p.termsConditions, 'Terms & conditions'],
    [!!p.logoUrl, 'Business logo'],
    [!!p.website, 'Website'],
    [!!p.tagline, 'Tagline'],
  ];
  const done = checks.filter(([v]) => v).length;
  const missing = checks.filter(([v]) => !v).map(([, l]) => l);
  return { pct: Math.round((done / checks.length) * 100), missing };
}

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors";
const labelCls = "block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5";

function Section({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-[var(--border)]">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-[var(--muted-foreground)]" style={{ backgroundColor: 'var(--muted)' }}>{icon}</div>
        <h2 className="font-bold text-[var(--foreground)] text-sm uppercase tracking-widest">{title}</h2>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

function NotifToggle({ label, sub, checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="w-full flex items-center justify-between gap-4 px-5 py-4 border-b border-[var(--border)] last:border-0 hover:bg-[var(--muted)]/30 transition-colors text-left">
      <div>
        <div className="text-sm font-semibold text-[var(--foreground)]">{label}</div>
        <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{sub}</div>
      </div>
      <div className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${checked ? '' : 'bg-[var(--border)]'}`} style={checked ? { backgroundColor: 'var(--accent)' } : {}}>
        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
      </div>
    </button>
  );
}

function AgentCardPreview({ profile, logoPreview }) {
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'Your Name';
  const biz = profile.businessName || 'Your Business';
  const logo = logoPreview || profile.logoUrl;
  return (
    <div className="rounded-xl border border-[var(--border)] overflow-hidden" style={{ backgroundColor: 'var(--muted)' }}>
      <div className="px-3 py-2 border-b border-[var(--border)]">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-foreground)]">Preview — as seen on quotes &amp; emails</p>
      </div>
      <div className="p-4">
        <div className="bg-white rounded-xl border border-[var(--border)] p-4">
          <div className="flex items-center gap-3 mb-3">
            {logo ? (
              <img src={logo} alt="Logo" className="w-10 h-10 rounded-lg object-contain border border-[var(--border)] bg-white" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ backgroundColor: 'var(--primary)' }}>{initials(profile.firstName, profile.lastName)}</div>
            )}
            <div className="min-w-0">
              <div className="font-bold text-[var(--foreground)] text-sm truncate">{name}</div>
              <div className="text-xs font-medium truncate" style={{ color: 'var(--accent)' }}>{biz}</div>
              {profile.tagline && <div className="text-[10px] text-[var(--muted-foreground)] truncate italic">{profile.tagline}</div>}
            </div>
          </div>
          <div className="space-y-1.5 text-xs text-[var(--muted-foreground)]">
            {profile.email && <div className="truncate">📧 {profile.email}</div>}
            {profile.phone && <div>📞 {profile.phone}</div>}
            {profile.website && <div className="truncate">🌐 {profile.website}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

const AgentProfile = () => {
  const { user, checkAuth } = useAuth();
  const isIndependent = user?.role === 'independent_agent';

  const [profileData, setProfileData] = useState({
    firstName: '', lastName: '', email: '', phone: '', businessName: '',
    website: '', tagline: '', termsConditions: '', backgroundTheme: 'none',
    logoUrl: '', commissionPercentage: 100,
    notifyPaymentReceived: true, notifyReminderSent: true, notifyQuoteViewed: true, notifyClientMessage: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  // Two-Factor Authentication method (email/sms/both) - the backend toggle
  // (PUT /api/auth/2fa/preferences) already existed but had no UI anywhere
  // to actually change it (found during the full-CRM audit, 2026-09).
  const [twoFaEnabled, setTwoFaEnabled] = useState(true);
  const [twoFaMethod, setTwoFaMethod] = useState('email');
  const [savingTwoFa, setSavingTwoFa] = useState(false);
  const [twoFaMessage, setTwoFaMessage] = useState('');

  useEffect(() => { loadProfile(); loadTwoFaSettings(); }, []);

  const loadTwoFaSettings = async () => {
    try {
      const data = await authService.getProfile();
      setTwoFaEnabled(data.two_fa_enabled ?? true);
      setTwoFaMethod(data.two_fa_method || 'email');
    } catch (error) {
      console.error('Error loading 2FA settings:', error);
    }
  };

  const handleSaveTwoFa = async (nextMethod) => {
    setSavingTwoFa(true);
    setTwoFaMessage('');
    try {
      await authService.updateTwoFactorPreferences({ enabled: twoFaEnabled, method: nextMethod });
      setTwoFaMethod(nextMethod);
      setTwoFaMessage('Two-factor authentication method updated.');
    } catch (error) {
      console.error('Error updating 2FA method:', error);
      setTwoFaMessage(error.response?.data?.message || 'Failed to update 2FA method.');
    } finally {
      setSavingTwoFa(false);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await agentsService.getProfile();
      setProfileData({
        firstName: data.firstName || data.first_name || '',
        lastName: data.lastName || data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        businessName: data.businessName || data.business_name || '',
        website: data.website || '',
        tagline: data.tagline || '',
        termsConditions: data.termsConditions || data.terms_conditions || '',
        backgroundTheme: data.backgroundTheme || data.background_theme || 'none',
        logoUrl: data.logoUrl || data.logo_url || '',
        commissionPercentage: data.commissionPercentage || data.commission_percentage || 100,
        notifyPaymentReceived: data.notifyPaymentReceived ?? true,
        notifyReminderSent: data.notifyReminderSent ?? true,
        notifyQuoteViewed: data.notifyQuoteViewed ?? true,
        notifyClientMessage: data.notifyClientMessage ?? false,
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    return phoneRegex.test(phone) && phone.replace(/[\s\-\(\)\+]/g, '').length >= 10;
  };

  const set = (key, value) => setProfileData(prev => ({ ...prev, [key]: value }));

  const handleThemeSelect = (themeId) => {
    set('backgroundTheme', themeId);
    applyBackgroundTheme(themeId);
  };

  const applyBackgroundTheme = (themeId) => {
    localStorage.setItem('agentBackgroundTheme', themeId);
    window.dispatchEvent(new CustomEvent('backgroundThemeChanged', { detail: { theme: themeId } }));
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await fetch('/api/agents/logo', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        set('logoUrl', data.logoUrl);
        setLogoFile(null);
        setLogoPreview(null);
        setMessage('Logo uploaded successfully!');
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage('Failed to upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setMessage('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { setMessage('Logo file must be less than 2MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    if (profileData.phone && !validatePhoneNumber(profileData.phone)) {
      setMessage('Enter a phone number with at least 10 digits (numbers, spaces, hyphens, parentheses and + only)');
      setSaving(false);
      return;
    }

    try {
      if (logoFile) await handleLogoUpload(logoFile);
      await agentsService.updateProfile(profileData);
      setMessage('Profile updated successfully!');
      applyBackgroundTheme(profileData.backgroundTheme);
      await checkAuth({ silent: true });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    if (passwordData.new_password.length < 8) {
      setPasswordMessage('New password must be at least 8 characters long');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordMessage('New password and confirmation do not match');
      return;
    }
    try {
      setChangingPassword(true);
      await agentsService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordMessage('Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordMessage(error.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <div className="voyager-app flex items-center justify-center min-h-[400px] text-sm text-[var(--muted-foreground)]">Loading profile…</div>;
  }

  const { pct, missing } = completeness(profileData);

  return (
    <div className="voyager-app p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">My Profile</h1>
          <p className="text-[var(--muted-foreground)] text-sm">Manage your business info, branding, and notification preferences.</p>
        </div>

        {message && <div className="bg-[var(--muted)]/60 text-[var(--foreground)] text-sm rounded-xl px-4 py-3">{message}</div>}

        {/* Completeness meter */}
        <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-[var(--foreground)]">Profile Completeness</span>
            <span className="text-sm font-bold" style={{ color: 'var(--accent)' }}>{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-[var(--muted)] overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: 'var(--accent)' }} />
          </div>
          {missing.length > 0 && (
            <p className="text-xs text-[var(--muted-foreground)] mt-2">Missing: {missing.join(', ')}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-5 items-start">
          <div className="space-y-5">
            <Section title="Personal Information" icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>}>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>First Name *</label><input className={inputCls} value={profileData.firstName} onChange={e => set('firstName', e.target.value)} required /></div>
                <div><label className={labelCls}>Last Name *</label><input className={inputCls} value={profileData.lastName} onChange={e => set('lastName', e.target.value)} required /></div>
                <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={profileData.email} onChange={e => set('email', e.target.value)} required /></div>
                <div>
                  <label className={labelCls}>Phone *</label>
                  <input type="tel" className={inputCls} value={profileData.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" required />
                </div>
              </div>
            </Section>

            <Section title="Business Information" icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>}>
              <div className="space-y-3">
                <div><label className={labelCls}>Business Name</label><input className={inputCls} value={profileData.businessName} onChange={e => set('businessName', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Website</label><input className={inputCls} value={profileData.website} onChange={e => set('website', e.target.value)} placeholder="yourtravel.com" /></div>
                  <div><label className={labelCls}>Tagline</label><input className={inputCls} value={profileData.tagline} onChange={e => set('tagline', e.target.value)} placeholder="Making dream trips happen" /></div>
                </div>
                {isIndependent && (
                  <div>
                    <label className={labelCls}>Commission Percentage</label>
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" max="100" step="0.01" className="w-32 px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none" value={profileData.commissionPercentage} onChange={e => set('commissionPercentage', e.target.value)} />
                      <span className="text-sm text-[var(--foreground)]">%</span>
                    </div>
                    <p className="text-xs text-[var(--muted-foreground)] mt-1">Used to calculate your commission from the gross amount. Changes only affect new commissions.</p>
                  </div>
                )}
                <div>
                  <label className={labelCls}>Terms &amp; Conditions for Quotes</label>
                  <textarea rows={4} className={inputCls} value={profileData.termsConditions} onChange={e => set('termsConditions', e.target.value)} placeholder="Enter your custom terms and conditions that will appear on all your quotes…" />
                  <p className="text-xs text-[var(--muted-foreground)] mt-1">If left blank, a default message will be used.</p>
                </div>
              </div>
            </Section>

            <Section title="Logo for Quotes" icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>}>
              <p className="text-sm text-[var(--muted-foreground)] mb-3">Upload your logo to display on quotes sent to clients. PNG or JPG, max 2MB.</p>
              {(logoPreview || profileData.logoUrl) && (
                <div className="mb-3 p-3 border border-[var(--border)] rounded-xl bg-[var(--muted)]/40 inline-block">
                  <img src={logoPreview || profileData.logoUrl} alt="Current Logo" className="max-h-16 max-w-40 object-contain" />
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleLogoFileChange} className="block w-full text-sm text-[var(--muted-foreground)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[var(--muted)] file:text-[var(--foreground)] hover:file:bg-[var(--border)]" />
              {logoFile && (
                <button type="button" onClick={() => handleLogoUpload(logoFile)} disabled={logoUploading} className="mt-3 px-4 py-2 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50" style={{ backgroundColor: 'var(--accent)' }}>
                  {logoUploading ? 'Uploading…' : 'Upload Logo'}
                </button>
              )}
            </Section>

            <Section title="Notification Preferences" icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>}>
              <div className="border border-[var(--border)] rounded-xl overflow-hidden -mx-1">
                <NotifToggle label="Payment Received" sub="Get notified when a client completes a payment" checked={profileData.notifyPaymentReceived} onChange={v => set('notifyPaymentReceived', v)} />
                <NotifToggle label="Reminder Sent" sub="Get notified when a scheduled payment reminder goes out" checked={profileData.notifyReminderSent} onChange={v => set('notifyReminderSent', v)} />
                <NotifToggle label="Quote Viewed" sub="Get notified when a client opens a quote you sent" checked={profileData.notifyQuoteViewed} onChange={v => set('notifyQuoteViewed', v)} />
                <NotifToggle label="Client Message" sub="Get notified when a client replies to a quote or trip" checked={profileData.notifyClientMessage} onChange={v => set('notifyClientMessage', v)} />
              </div>
            </Section>

            <Section title="Desktop Background Theme" icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>}>
              <p className="text-sm text-[var(--muted-foreground)] mb-4">Choose a background gradient or pattern shown on the sides of your screen on laptops and desktops. Doesn't affect mobile.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {backgroundThemes.map(theme => (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => handleThemeSelect(theme.id)}
                    className={`relative h-16 rounded-xl border-2 overflow-hidden transition-all ${profileData.backgroundTheme === theme.id ? 'border-[var(--accent)]' : 'border-[var(--border)]'}`}
                    style={{ background: theme.preview === 'transparent' ? 'var(--muted)' : theme.preview }}
                    title={theme.name}
                  >
                    <span className="absolute bottom-1 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/40 text-white">{theme.name}</span>
                    {profileData.backgroundTheme === theme.id && (
                      <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-white flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </Section>

            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50" style={{ backgroundColor: 'var(--primary)' }}>
                {saving ? 'Saving…' : 'Save Profile'}
              </button>
            </div>
          </div>

          <div className="space-y-5">
            <AgentCardPreview profile={profileData} logoPreview={logoPreview} />
          </div>
        </form>

        <Section title="Security" icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>}>
          <div className="mb-6 pb-6 border-b border-[var(--border)]">
            <h3 className="text-sm font-bold text-[var(--foreground)] mb-1">Two-Factor Authentication</h3>
            <p className="text-xs text-[var(--muted-foreground)] mb-3">Choose how you receive your login verification code.</p>
            {twoFaMessage && <div className="mb-3 text-sm px-3 py-2.5 rounded-xl bg-[var(--muted)]/60 text-[var(--foreground)]">{twoFaMessage}</div>}
            <div className="inline-flex rounded-xl border border-[var(--border)] overflow-hidden">
              {[
                { id: 'email', label: '📧 Email' },
                { id: 'sms', label: '📱 SMS' },
                { id: 'both', label: '🔐 Both' },
              ].map((opt, i) => (
                <button
                  key={opt.id}
                  type="button"
                  disabled={savingTwoFa || !profileData.phone}
                  onClick={() => handleSaveTwoFa(opt.id)}
                  className={`px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${i > 0 ? 'border-l border-[var(--border)]' : ''}`}
                  style={twoFaMethod === opt.id
                    ? { backgroundColor: 'var(--accent)', color: 'white' }
                    : { backgroundColor: 'white', color: 'var(--foreground)' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {!profileData.phone && (
              <p className="text-xs mt-2" style={{ color: 'var(--accent)' }}>Add a phone number above to enable SMS verification.</p>
            )}
          </div>

          {passwordMessage && <div className="mb-4 text-sm px-3 py-2.5 rounded-xl bg-[var(--muted)]/60 text-[var(--foreground)]">{passwordMessage}</div>}
          <form onSubmit={handleChangePassword} className="space-y-3 max-w-sm">
            <div>
              <label className={labelCls}>Current Password</label>
              <input type="password" className={inputCls} value={passwordData.current_password} onChange={e => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))} required />
            </div>
            <div>
              <label className={labelCls}>New Password</label>
              <input type="password" className={inputCls} value={passwordData.new_password} onChange={e => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))} required minLength={8} />
            </div>
            <div>
              <label className={labelCls}>Confirm New Password</label>
              <input type="password" className={inputCls} value={passwordData.confirm_password} onChange={e => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))} required minLength={8} />
            </div>
            <button type="submit" disabled={changingPassword} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50" style={{ backgroundColor: 'var(--primary)' }}>
              {changingPassword ? 'Changing…' : 'Change Password'}
            </button>
          </form>
        </Section>
      </div>
    </div>
  );
};

export default AgentProfile;
